import { RelayClient, deriveDepositWallet } from '@polymarket/builder-relayer-client'
import { getContractConfig } from '@polymarket/builder-relayer-client/dist/config'
import { BuilderConfig } from '@polymarket/builder-signing-sdk'
import { AssetType, ClobClient, SignatureTypeV2 } from '@polymarket/clob-client-v2'
import type { ApiKeyCreds } from '@polymarket/clob-client-v2'
import { encodeFunctionData, erc20Abi, maxUint256 } from 'viem'
import type { Address, PublicClient, WalletClient } from 'viem'

import {
	CLOB_API_URL_V2,
	POLYGON_CHAIN_ID,
	POLYMARKET_EXCHANGE_V2,
	POLYMARKET_NEG_RISK_EXCHANGE_V2,
	POLY_BUILDER_CODE,
	RELAYER_URL,
} from '@/constants/polymarket'
import {
	COLLATERAL_ONRAMP_CONTRACT_ADDRESS,
	CTF_COLLATERAL_ADAPTER_ADDRESS,
	CTF_CONTRACT_ADDRESS,
	NEG_RISK_ADAPTER_ADDRESS,
	NEG_RISK_CTF_COLLATERAL_ADAPTER_ADDRESS,
	PUSD_CONTRACT_ADDRESS,
	USDC_E_CONTRACT_ADDRESS,
	USDC_E_DECIMALS,
} from '@/constants/tokens'
import { readViemErc20Balance } from '@/lib/wallets/evm'
import { checkAllApprovals } from '@/utils/approvals'

const DEPOSIT_WALLET_BATCH_DEADLINE_SECONDS = 600

/**
 * Derives the deterministic deposit wallet address for a given EOA.
 * This is a pure computation based on CREATE2, no RPC calls or state required.
 * @param eoa - The owner's address (0x-prefixed hex string)
 * @returns The deterministic deposit wallet address
 */
export function deriveDepositWalletAddress(eoa: Address | string): Address {
	const contractConfig = getContractConfig(POLYGON_CHAIN_ID)
	const depositWalletConfig = contractConfig.DepositWalletContracts

	const address = deriveDepositWallet(
		eoa as `0x${string}`,
		depositWalletConfig.DepositWalletFactory,
		depositWalletConfig.DepositWalletImplementation
	)

	return address as Address
}

export async function isDepositWalletDeployed(
	client: PublicClient,
	address: Address
): Promise<boolean> {
	const code = await client.getCode({ address: address as `0x${string}` })
	return code !== undefined && code !== '0x'
}

export interface DepositWalletCall {
	target: Address
	value: string
	data: `0x${string}`
}

interface PolymarketApprovalStatus {
	allApproved: boolean
	pusdApprovals: Record<string, boolean>
	usdcEApprovals: Record<string, boolean>
	outcomeTokenApprovals: Record<string, boolean>
}

export function buildErc20TransferCall(
	token: Address,
	to: Address,
	amount: bigint
): DepositWalletCall {
	return {
		target: token,
		value: '0',
		data: encodeFunctionData({
			abi: erc20Abi,
			functionName: 'transfer',
			args: [to as `0x${string}`, amount],
		}),
	}
}

export function buildErc20ApproveCall(
	token: Address,
	spender: Address,
	amount: bigint = maxUint256
): DepositWalletCall {
	return {
		target: token,
		value: '0',
		data: encodeFunctionData({
			abi: erc20Abi,
			functionName: 'approve',
			args: [spender as `0x${string}`, amount],
		}),
	}
}

const erc1155SetApprovalAbi = [
	{
		inputs: [
			{ name: 'operator', type: 'address' },
			{ name: 'approved', type: 'bool' },
		],
		name: 'setApprovalForAll',
		outputs: [],
		stateMutability: 'nonpayable',
		type: 'function',
	},
] as const

export function buildErc1155SetApprovalForAllCall(
	token: Address,
	operator: Address,
	approved: boolean
): DepositWalletCall {
	return {
		target: token,
		value: '0',
		data: encodeFunctionData({
			abi: erc1155SetApprovalAbi,
			functionName: 'setApprovalForAll',
			args: [operator as `0x${string}`, approved],
		}),
	}
}

// Mirrors the spender sets in src/utils/approvals.ts. Keep names in sync too:
// buildMissingPolymarketApprovalCalls indexes checkAllApprovals() by name.
const PUSD_APPROVAL_CALLS = [
	{
		name: 'V2 Exchange',
		build: () => buildErc20ApproveCall(PUSD_CONTRACT_ADDRESS, POLYMARKET_EXCHANGE_V2),
	},
	{
		name: 'V2 Neg Risk Exchange',
		build: () => buildErc20ApproveCall(PUSD_CONTRACT_ADDRESS, POLYMARKET_NEG_RISK_EXCHANGE_V2),
	},
	{
		name: 'CTF Collateral Adapter',
		build: () => buildErc20ApproveCall(PUSD_CONTRACT_ADDRESS, CTF_COLLATERAL_ADAPTER_ADDRESS),
	},
	{
		name: 'Neg Risk CTF Collateral Adapter',
		build: () =>
			buildErc20ApproveCall(PUSD_CONTRACT_ADDRESS, NEG_RISK_CTF_COLLATERAL_ADAPTER_ADDRESS),
	},
	{
		name: 'Neg Risk Adapter',
		build: () => buildErc20ApproveCall(PUSD_CONTRACT_ADDRESS, NEG_RISK_ADAPTER_ADDRESS),
	},
] as const

const USDC_E_APPROVAL_CALLS = [
	{
		name: 'Collateral Onramp',
		build: () => buildErc20ApproveCall(USDC_E_CONTRACT_ADDRESS, COLLATERAL_ONRAMP_CONTRACT_ADDRESS),
	},
] as const

const OUTCOME_TOKEN_APPROVAL_CALLS = [
	{
		name: 'V2 Exchange',
		build: () =>
			buildErc1155SetApprovalForAllCall(CTF_CONTRACT_ADDRESS, POLYMARKET_EXCHANGE_V2, true),
	},
	{
		name: 'V2 Neg Risk Exchange',
		build: () =>
			buildErc1155SetApprovalForAllCall(
				CTF_CONTRACT_ADDRESS,
				POLYMARKET_NEG_RISK_EXCHANGE_V2,
				true
			),
	},
	{
		name: 'CTF Collateral Adapter',
		build: () =>
			buildErc1155SetApprovalForAllCall(CTF_CONTRACT_ADDRESS, CTF_COLLATERAL_ADAPTER_ADDRESS, true),
	},
	{
		name: 'Neg Risk Adapter',
		build: () =>
			buildErc1155SetApprovalForAllCall(CTF_CONTRACT_ADDRESS, NEG_RISK_ADAPTER_ADDRESS, true),
	},
] as const

export function buildPolymarketApprovalCalls(): DepositWalletCall[] {
	return [
		...PUSD_APPROVAL_CALLS.map(({ build }) => build()),
		...USDC_E_APPROVAL_CALLS.map(({ build }) => build()),
		...OUTCOME_TOKEN_APPROVAL_CALLS.map(({ build }) => build()),
	]
}

export function buildMissingPolymarketApprovalCalls(
	status: PolymarketApprovalStatus
): DepositWalletCall[] {
	return [
		...PUSD_APPROVAL_CALLS.filter(({ name }) => !status.pusdApprovals[name]).map(({ build }) =>
			build()
		),
		...USDC_E_APPROVAL_CALLS.filter(({ name }) => !status.usdcEApprovals[name]).map(({ build }) =>
			build()
		),
		...OUTCOME_TOKEN_APPROVAL_CALLS.filter(({ name }) => !status.outcomeTokenApprovals[name]).map(
			({ build }) => build()
		),
	]
}

// ABI matches buildWrapTx in src/utils/wrap-collateral.ts (param names _asset/_to/_amount).
const collateralOnrampWrapAbi = [
	{
		name: 'wrap',
		type: 'function',
		stateMutability: 'nonpayable',
		inputs: [
			{ name: '_asset', type: 'address' },
			{ name: '_to', type: 'address' },
			{ name: '_amount', type: 'uint256' },
		],
		outputs: [],
	},
] as const

/**
 * Builds a deposit wallet call to wrap USDC.e into pUSD via the Collateral Onramp.
 * Mirrors buildWrapTx in src/utils/wrap-collateral.ts but returns a DepositWalletCall.
 * Caller must have already approved the Onramp to spend USDC.e (see buildPolymarketApprovalCalls).
 */
export function buildWrapUsdceToPusdCall(
	depositWallet: Address,
	amount: bigint
): DepositWalletCall {
	return {
		target: COLLATERAL_ONRAMP_CONTRACT_ADDRESS as Address,
		value: '0',
		data: encodeFunctionData({
			abi: collateralOnrampWrapAbi,
			functionName: 'wrap',
			args: [USDC_E_CONTRACT_ADDRESS as `0x${string}`, depositWallet as `0x${string}`, amount],
		}),
	}
}

// ─── CLOB integration ─────────────────────────────────────────────────────────

/**
 * Constructs an authenticated V2 ClobClient using POLY_1271 signature type,
 * binding it to the deposit wallet as the funder. The deposit wallet signs
 * orders via EIP-1271 (contract signature verification) rather than EOA ECDSA.
 */
export function createDepositWalletClobClient(params: {
	walletClient: WalletClient
	depositWalletAddress: Address
	creds: ApiKeyCreds
}): ClobClient {
	return new ClobClient({
		host: CLOB_API_URL_V2,
		chain: POLYGON_CHAIN_ID,
		// biome-ignore lint/suspicious/noExplicitAny: SDK accepts viem WalletClient at runtime
		signer: params.walletClient as any,
		creds: params.creds,
		signatureType: SignatureTypeV2.POLY_1271,
		funderAddress: params.depositWalletAddress,
		builderConfig: POLY_BUILDER_CODE ? { builderCode: POLY_BUILDER_CODE } : undefined,
	})
}

/**
 * Syncs the CLOB's internal balance/allowance snapshot for the deposit wallet.
 * Must be called after approvals are set so the CLOB sees the on-chain state.
 * Syncs collateral (pUSD) once, then any conditional token IDs provided.
 */
export async function syncDepositWalletClobBalances(params: {
	clob: ClobClient
	conditionalTokenIds?: string[]
}): Promise<void> {
	await params.clob.updateBalanceAllowance({
		// biome-ignore lint/style/useNamingConvention: SDK BalanceAllowanceParams uses snake_case
		asset_type: AssetType.COLLATERAL,
	})
	if (params.conditionalTokenIds && params.conditionalTokenIds.length > 0) {
		for (const tokenId of params.conditionalTokenIds) {
			await params.clob.updateBalanceAllowance({
				// biome-ignore lint/style/useNamingConvention: SDK BalanceAllowanceParams uses snake_case
				asset_type: AssetType.CONDITIONAL,
				// biome-ignore lint/style/useNamingConvention: SDK BalanceAllowanceParams uses snake_case
				token_id: tokenId,
			})
		}
	}
}

// ─── Lifecycle helpers ────────────────────────────────────────────────────────

const REMOTE_SIGNING_URL = () =>
	`${process.env.NEXT_PUBLIC_APP_URL || 'http://localhost:3006'}/api/polymarket/trading`

function buildClientRelayClient(walletClient: WalletClient, relayerUrl = RELAYER_URL): RelayClient {
	const builderConfig = new BuilderConfig({
		remoteBuilderConfig: { url: REMOTE_SIGNING_URL() },
	})
	return new RelayClient(
		relayerUrl,
		POLYGON_CHAIN_ID,
		// biome-ignore lint/suspicious/noExplicitAny: SDK accepts viem WalletClient at runtime
		walletClient as any,
		builderConfig
	)
}

async function isDepositWalletDeployedViaRelayer(
	relayer: RelayClient,
	depositWalletAddress: Address
): Promise<boolean> {
	try {
		return await relayer.getDeployed(depositWalletAddress, 'WALLET')
	} catch {
		return false
	}
}

export interface DepositWalletDeployResult {
	transactionHash: string
	address: Address
}

/**
 * Deploys the deposit wallet for the given viem WalletClient via the Polymarket
 * relayer (gas sponsored). Returns the confirmed tx hash and the deterministic
 * deposit wallet address.
 *
 * This is a client-side helper — call from the browser after the user connects
 * their wallet. Server-side callers use the ethers-wallet equivalent in execute.ts.
 */
export async function deployDepositWallet(
	walletClient: WalletClient,
	relayerUrl = RELAYER_URL
): Promise<DepositWalletDeployResult> {
	const relayer = buildClientRelayClient(walletClient, relayerUrl)
	const response = await relayer.deployDepositWallet()
	const confirmed = await response.wait()
	if (!confirmed) {
		throw new Error(`Deposit wallet deploy failed or timed out: ${response.transactionID}`)
	}
	// deriveDepositWalletAddress() on RelayClient is async (fetches chain config)
	const address = (await relayer.deriveDepositWalletAddress()) as Address
	return {
		transactionHash: confirmed?.transactionHash ?? response.transactionHash,
		address,
	}
}

/**
 * Executes a batch of calls on the deposit wallet via the Polymarket relayer.
 * Gas is sponsored. Official relayer rejects short windows, so default to the
 * 10-minute deadline used in Polymarket's deposit-wallet docs.
 */
export async function executeDepositWalletBatch(
	walletClient: WalletClient,
	calls: DepositWalletCall[],
	depositWalletAddress: Address,
	deadlineSeconds = DEPOSIT_WALLET_BATCH_DEADLINE_SECONDS,
	relayerUrl = RELAYER_URL
): Promise<{ transactionHash: string }> {
	const relayer = buildClientRelayClient(walletClient, relayerUrl)
	const deadline = Math.floor(Date.now() / 1000 + deadlineSeconds).toString()
	const response = await relayer.executeDepositWalletBatch(
		calls,
		depositWalletAddress as `0x${string}`,
		deadline
	)
	const confirmed = await response.wait()
	if (!confirmed) {
		throw new Error(`Deposit wallet batch failed or timed out: ${response.transactionID}`)
	}
	return {
		transactionHash: confirmed?.transactionHash ?? response.transactionHash,
	}
}

/**
 * Idempotent: ensures the deposit wallet is deployed, approved, and any
 * USDC.e balance is wrapped to pUSD.
 *
 * Steps (each skipped if already satisfied):
 *   1. Deploy if bytecode is absent
 *   2. Set all Polymarket approvals if any are missing; re-read to confirm
 *   3. Wrap any USDC.e sitting on the deposit wallet to pUSD
 *
 * Returns booleans indicating which actions were taken (true = action was
 * needed and executed).
 */
export async function ensureDepositWalletReady(params: {
	walletClient: WalletClient
	publicClient: PublicClient
	depositWalletAddress: Address
	relayerUrl?: string
}): Promise<{ deployed: boolean; approved: boolean; wrapped: boolean }> {
	const { walletClient, publicClient, depositWalletAddress, relayerUrl = RELAYER_URL } = params
	const relayer = buildClientRelayClient(walletClient, relayerUrl)

	const [deployedOnChain, deployedInRelayer] = await Promise.all([
		isDepositWalletDeployed(publicClient, depositWalletAddress),
		isDepositWalletDeployedViaRelayer(relayer, depositWalletAddress),
	])
	const wasDeployed = deployedOnChain || deployedInRelayer
	if (!wasDeployed) {
		try {
			await deployDepositWallet(walletClient, relayerUrl)
		} catch (err) {
			const deployedAfterFailure =
				(await isDepositWalletDeployed(publicClient, depositWalletAddress)) ||
				(await isDepositWalletDeployedViaRelayer(relayer, depositWalletAddress))
			if (!deployedAfterFailure) {
				throw err
			}
		}
	}

	const approvalStatus = await checkAllApprovals(depositWalletAddress)
	if (!approvalStatus.allApproved) {
		const approvalCalls = buildMissingPolymarketApprovalCalls(approvalStatus)
		if (approvalCalls.length > 0) {
			await executeDepositWalletBatch(
				walletClient,
				approvalCalls,
				depositWalletAddress,
				DEPOSIT_WALLET_BATCH_DEADLINE_SECONDS,
				relayerUrl
			)
		}
		const recheck = await checkAllApprovals(depositWalletAddress)
		if (!recheck.allApproved) {
			throw new Error(
				`Deposit wallet approvals tx mined but on-chain allowances still missing for ${depositWalletAddress}`
			)
		}
	}

	// Wrap any USDC.e sitting on the deposit wallet to pUSD.
	const usdceBalance = await readViemErc20Balance({
		client: publicClient,
		tokenAddress: USDC_E_CONTRACT_ADDRESS,
		account: depositWalletAddress as `0x${string}`,
		decimals: USDC_E_DECIMALS,
		symbol: 'USDC.e',
	})
	const usdceToWrap = usdceBalance.raw
	if (usdceToWrap > 0n) {
		await executeDepositWalletBatch(
			walletClient,
			[buildWrapUsdceToPusdCall(depositWalletAddress, usdceToWrap)],
			depositWalletAddress,
			DEPOSIT_WALLET_BATCH_DEADLINE_SECONDS,
			relayerUrl
		)
	}

	return {
		deployed: !wasDeployed,
		approved: !approvalStatus.allApproved,
		wrapped: usdceToWrap > 0n,
	}
}
